import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Search } from "lucide-react";
import { api } from "@/lib/api";
import { PROFESSIONAL_MAP_SECTIONS } from "@/data/professionalMapData";

const MODES = [
  { key: "countries", label: "Страны" },
  { key: "cities", label: "Города" },
  { key: "categories", label: "Категории" },
];
const initialGeography = { countries: [], cities: [] };

// Country artwork is an optional, approved static asset. City names remain data-driven.
const COUNTRY_ART = { "Литва": "/images/professional-map/countries/lithuania-1200x1500.webp" };
function CountryCard({ place, onOpen, cities, onCity }) {
  const [imageFailed, setImageFailed] = useState(false);
  const art = COUNTRY_ART[place.name];
  return (
    <article className="min-w-0" data-testid="pm-country-card">
      <button type="button" onClick={onOpen} aria-label={"Смотреть города: " + place.name}
        className="group relative block aspect-[4/5] w-full overflow-hidden rounded-[22px] border border-[#BBA48D]/35 bg-gradient-to-br from-[#F8E9D6] to-[#DCC5A9] text-left shadow-[0_8px_24px_rgba(73,52,36,0.09)] transition-shadow hover:shadow-[0_14px_30px_rgba(73,52,36,0.16)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#755F4E]">
        {art && !imageFailed && <img src={art} alt="" loading="lazy"
          onError={() => setImageFailed(true)}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025]" />}
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#24180F]/85 via-[#24180F]/30 to-transparent" />
        <span className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-3 text-white sm:p-5">
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/85">Страна</span>
          <span className="font-serif text-2xl leading-tight sm:text-3xl">{place.name}</span>
          <span className="flex items-center justify-between gap-1 text-xs font-medium sm:text-sm">
            Смотреть города <ArrowUpRight size={19} strokeWidth={1.8} aria-hidden="true" />
          </span>
        </span>
      </button>
      {cities.length > 0 && <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2" aria-label={"Города страны " + place.name}>
        {cities.map((item) => <button key={item.name} type="button"
          onClick={() => onCity(item.name)}
          className="min-h-9 text-left text-sm text-[#654B38] underline decoration-[#B89A7C]/50 underline-offset-4 hover:text-[#332318] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#755F4E]">
          {item.name}
        </button>)}
      </div>}
    </article>
  );
}

export default function ProfessionalMapDiscovery({ ownerPreview = false }) {
  const [params, setParams] = useSearchParams();
  const view = MODES.some(({ key }) => key === params.get("view")) ? params.get("view") : "countries";
  const chosenSection = PROFESSIONAL_MAP_SECTIONS.find((s) => s.slug === params.get("section"));
  const section = chosenSection?.slug || "";
  const country = (params.get("country") || "").slice(0, 100);
  const textQuery = (params.get("q") || "").slice(0, 180);
  const city = (params.get("city") || "").slice(0, 100);
  const category = (params.get("category") || "").slice(0, 100);
  const categoryData = chosenSection?.samples.find((s) => s.title === category);
  const [geography, setGeography] = useState({ status: "loading", data: initialGeography });
  const [listing, setListing] = useState({ status: "idle", data: [], total: 0 });
  const [term, setTerm] = useState(textQuery);
  useEffect(() => { setTerm(textQuery); }, [textQuery]);

  const locations = useMemo(() => (
    view === "countries" ? geography.data.countries : geography.data.cities
  ), [view, geography.data]);
  const shouldSearch = Boolean(country || city || textQuery || (view === "categories" && categoryData));
  const roleFilters = (view === "categories" && categoryData?.roles) || [];

  useEffect(() => {
    let live = true;
    setGeography({ status: "loading", data: initialGeography });
    const query = new URLSearchParams();
    if (section) query.set("section", section);
    // The same interface can be checked without payment, but ONLY by Owner
    // through the separately authenticated, owner-scoped draft endpoint.
    const request = ownerPreview
      ? api.get("/professional-map/cards/owner-qa/search?" +
          new URLSearchParams({ section, limit: "50" }).toString())
      : api.get("/professional-map/geography?" + query.toString());
    request.then(({ data }) => {
        if (!live) return;
        if (ownerPreview) {
          const countries = new Map();
          const cities = new Map();
          for (const card of (data?.results || [])) {
            if (card.country) {
              const existing = countries.get(card.country) || { name: card.country, count: 0 };
              existing.count += 1;
              countries.set(card.country, existing);
            }
            if (card.country && card.city) {
              const identity = card.country + ":" + card.city;
              const existing = cities.get(identity) || { name: card.city, country: card.country, count: 0 };
              existing.count += 1;
              cities.set(identity, existing);
            }
          }
          setGeography({ status: "success", data: {
            countries: [...countries.values()].sort((a,b) => a.name.localeCompare(b.name, "ru")),
            cities: [...cities.values()].sort((a,b) => a.name.localeCompare(b.name, "ru")),
          } });
        } else {
          setGeography({
            status: "success",
            data: {
              countries: Array.isArray(data?.countries) ? data.countries : [],
              cities: Array.isArray(data?.cities) ? data.cities : [],
            },
          });
        }
      })
      .catch(() => { if (live) setGeography({ status: "unavailable", data: initialGeography }); });
    return () => { live = false; };
  }, [section, ownerPreview]);

  useEffect(() => {
    if (!shouldSearch) {
      setListing({ status: "idle", data: [], total: 0 });
      return;
    }
    let live = true;
    setListing({ status: "loading", data: [], total: 0 });
    const query = new URLSearchParams();
    if (section) query.set("section", section);
    if (country) query.set("country", country);
    if (city) query.set("city", city);
    if (textQuery) query.set("q", textQuery);
    if (view === "categories" && categoryData) {
      if (roleFilters.length) {
        roleFilters.forEach((role) => query.append("specializations", role));
      } else if (!textQuery) {
        // Offer classification is not yet in the public search schema.
        // Search verified published profiles by the selected category's real text.
        query.set("q", categoryData.title);
      }
    }
    query.set("limit", "30");
    const request = ownerPreview
      ? api.get("/professional-map/cards/owner-qa/search?" + query.toString())
      : api.get("/professional-map/search?" + query.toString());
    request.then(({ data }) => {
        if (!live) return;
        setListing({
          status: "success",
          data: Array.isArray(data?.results) ? data.results : [],
          total: Number(data?.total || 0),
        });
      })
      .catch(() => { if (live) setListing({ status: "unavailable", data: [], total: 0 }); });
    return () => { live = false; };
  }, [view, section, country, city, category, shouldSearch, textQuery, ownerPreview]);

  const move = (changes) => {
    const next = new URLSearchParams();
    next.set("view", changes.view || view);
    if (changes.section || (changes.section === undefined && section)) next.set("section", changes.section ?? section);
    if (changes.country) next.set("country", changes.country);
    if (changes.city) next.set("city", changes.city);
    if (changes.category) next.set("category", changes.category);
    const nextText = changes.q === undefined ? textQuery : changes.q;
    if (nextText) next.set("q", nextText);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-[#F7F0E5] text-[#3A2B22]" data-testid="professional-map-discovery">
      <main className="mx-auto max-w-[1100px] px-5 pb-20 pt-6 sm:px-8">
        <Link to={ownerPreview ? "/account/professional-cards" : "/professional-map"} className="inline-flex min-h-[44px] items-center gap-2 text-sm text-[#705C4D]">
          <ArrowLeft size={17} aria-hidden="true" /> {ownerPreview ? "Назад" : "К Профессиональной карте"}
        </Link>
        <div className="mt-6 rounded-[26px] border border-[#826751]/20 bg-white/75 px-5 py-7 sm:px-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A7463]">Профессиональная карта</p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl">Найти профессионалов</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#725D4E]">
            {ownerPreview
              ? "Так посетители будут выбирать город, страну и категорию. Здесь видны только твои сохранённые черновики — другим людям они недоступны."
              : "Выбирай город, страну или категорию. Здесь появляются только действующие опубликованные страницы."}
          </p>
          <nav aria-label="Способ поиска" className="mt-5 flex flex-wrap gap-2">
            {MODES.map(({ key, label }) => (
              <button key={key} type="button" onClick={() => move({ view: key })}
                className={`min-h-[44px] rounded-xl border px-4 py-2 text-sm ${view === key ? "border-[#69503E] bg-[#EDE0CE]" : "border-[#826751]/25 bg-white/75 hover:bg-white"}`}
                aria-current={view === key ? "page" : undefined} data-testid={`pm-discovery-tab-${key}`}>
                {label}
              </button>
            ))}
          </nav>
          <form onSubmit={(event) => { event.preventDefault(); move({ q: term.trim() }); }}
            className="mt-5 flex flex-wrap items-center gap-2" role="search" aria-label="Поиск по Профессиональной карте">
            <label className="sr-only" htmlFor="pm-discovery-term">Имя, профессия, услуга или товар</label>
            <input id="pm-discovery-term" type="search" value={term} maxLength={180}
              autoComplete="off" onChange={(event) => setTerm(event.target.value)}
              placeholder="Имя, профессия, услуга или товар"
              className="min-h-11 min-w-0 flex-1 rounded-xl border border-[#826751]/25 bg-white px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#826751]/20"
              data-testid="pm-discovery-text-search"/>
            <button type="submit" className="min-h-11 rounded-xl border border-[#826751]/25 bg-[#F4EADD] px-5 text-sm text-[#4A382B] hover:bg-[#E9DBC7]">
              Найти
            </button>
          </form>
        </div>
        <section className="mt-5 rounded-[24px] border border-[#826751]/20 bg-white/75 px-5 py-6 sm:px-8" aria-label="Навигация и фильтры">
          <div className="flex flex-wrap gap-2">
            {PROFESSIONAL_MAP_SECTIONS.map((s) => (
              <button key={s.slug} type="button" onClick={() => move({ section: s.slug, view })}
                className={`min-h-[40px] rounded-xl border px-3 py-2 text-xs ${section === s.slug ? "border-[#69503E] bg-[#EDE0CE]" : "border-[#826751]/20 hover:bg-[#F9F2E9]"}`}>
                {s.title}
              </button>
            ))}
            {section && <button type="button" onClick={() => move({ section: "", view })} className="min-h-[40px] rounded-xl border border-[#826751]/20 px-3 text-xs">Все направления</button>}
          </div>
          {view === "categories" ? (
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {(chosenSection ? [chosenSection] : PROFESSIONAL_MAP_SECTIONS).map((s) => (
                <div key={s.slug}>
                  <h2 className="font-serif text-xl">{s.title}</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.samples.map((item) => (
                      <button key={item.title} type="button"
                        onClick={() => move({ view: "categories", section: s.slug, category: item.title })}
                        className={`min-h-[40px] rounded-xl border px-3 py-2 text-xs ${section === s.slug && category === item.title ? "border-[#69503E] bg-[#EDE0CE]" : "border-[#826751]/20 hover:bg-[#F9F2E9]"}`}>
                        {item.title}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : geography.status === "loading" ? <p className="mt-6 text-sm" role="status">Загружаем доступные места…</p>
          : geography.status === "unavailable" ? <p className="mt-6 text-sm" role="status">Географический поиск временно недоступен.</p>
          : locations.length === 0 ? <p className="mt-6 text-sm" role="status">{ownerPreview ? "Среди твоих сохранённых страниц пока нет участников для этой подборки." : "Пока нет опубликованных участников для этой подборки."}</p>
          : view === "countries" ? <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-5" data-testid="pm-country-grid">
              {locations.map((place) => <CountryCard key={place.name} place={place}
                onOpen={() => move({ view: "countries", country: place.name, city: "" })}
                cities={geography.data.cities.filter((item) => item.country === place.name && item.name)}
                onCity={(name) => move({ view: "cities", country: place.name, city: name })} />)}
            </div>
          : <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3" data-testid="pm-city-links">
              {locations.map((place) => <button key={(place.country || "") + place.name} type="button"
                onClick={() => move({ view: "cities", country: place.country, city: place.name })}
                className="min-h-11 rounded-lg px-2 text-sm text-[#654B38] underline decoration-[#B89A7C]/50 underline-offset-4 hover:bg-[#F9F2E9]">
                {place.name}{place.country ? " · " + place.country : ""}
              </button>)}
            </div>}
        </section>
        {shouldSearch && <section className="mt-5 rounded-[24px] border border-[#826751]/20 bg-white/75 px-5 py-6 sm:px-8"
          aria-label="Опубликованные профессиональные страницы" data-testid="pm-discovery-results">
          <div className="flex items-center gap-2">
            <Search size={18} aria-hidden="true" />
            <h2 className="font-serif text-2xl">Результаты поиска</h2>
          </div>
          {listing.status === "loading" ? <p className="mt-4 text-sm" role="status">Ищем опубликованные страницы…</p>
          : listing.status === "unavailable" ? <p className="mt-4 text-sm" role="status">Сервис поиска временно недоступен.</p>
          : listing.data.length === 0 ? <p className="mt-4 text-sm" role="status">{ownerPreview ? "В твоих черновиках пока нет совпадений по выбранным условиям." : "По выбранным условиям пока нет опубликованных страниц."}</p>
          : <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {listing.data.filter((item) => item?.id && item?.public_name).map((item) => (
              <Link key={item.id} to={ownerPreview ? "/account/professional-cards?card=" + encodeURIComponent(item.id) : "/professional-map/profiles/" + encodeURIComponent(item.id)}
                className="rounded-xl border border-[#826751]/20 p-4 hover:bg-[#F9F2E9]">
                <p className="font-serif text-xl">{item.public_name}</p>
                <p className="mt-1 text-xs text-[#806B5A]">{[item.city, ownerPreview ? item.region : null, item.country].filter(Boolean).join(", ")}</p>
                {item.specializations?.length > 0 && <p className="mt-2 text-xs">{item.specializations.join(" · ")}</p>}
              </Link>
            ))}
          </div>}
          {listing.status === "success" && listing.total > listing.data.length &&
            <p className="mt-4 text-xs text-[#806B5A]">Показаны первые {listing.data.length} из {listing.total} результатов.</p>}
        </section>}
      </main>
    </div>
  );
}
